import type { ReactNode } from 'react';
import { Link, NavLink, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Layout } from './Layout.js';
import { Seo } from './Seo.js';
import { useAuthStore } from '../lib/auth.js';
import { useCurrentOrg } from '../lib/orgs.js';
import { api } from '../lib/api.js';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
}

export function DashboardShell({ children }: { children: ReactNode }) {
  const { lang } = useParams();
  const { t } = useTranslation('common');
  const { t: tD } = useTranslation('dashboard');
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const org = useCurrentOrg();

  const resendMutation = useMutation({
    mutationFn: () => api<{ ok: boolean }>('/auth/resend-verification', { method: 'POST' }),
  });

  const items: NavItem[] = [
    {
      to: `/${lang}/dashboard`,
      label: tD('nav.home', { defaultValue: 'Inicio' }),
      icon: <HomeIcon />,
    },
    {
      to: `/${lang}/dashboard/events`,
      label: t('nav.events'),
      icon: <CalendarIcon />,
    },
    {
      to: `/${lang}/dashboard/credits`,
      label: t('nav.credits'),
      icon: <CoinIcon />,
    },
    {
      to: `/${lang}/dashboard/settings`,
      label: t('nav.settings'),
      icon: <CogIcon />,
    },
  ];

  if (user?.isAdmin) {
    items.push({
      to: `/${lang}/dashboard/admin`,
      label: 'Admin',
      icon: <ShieldIcon />,
    });
  }

  return (
    <Layout>
      <Seo
        title={t('nav.dashboard')}
        description="Boletra dashboard"
        noindex
        locale={(lang as 'es' | 'en') ?? 'es'}
      />
      {!user?.emailVerifiedAt && (
        <div className="border-b border-amber-800/40 bg-amber-950/60 px-4 py-3">
          <div className="max-w-6xl mx-auto flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <div className="flex items-start gap-2 flex-1 min-w-0">
              <span className="mt-0.5 shrink-0 text-amber-400">
                <MailIcon />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium text-amber-300">
                  {tD('email_verification.banner_title')}
                </p>
                <p className="text-xs text-amber-400/80 mt-0.5">
                  {tD('email_verification.banner_body')}
                </p>
              </div>
            </div>
            <button
              type="button"
              disabled={resendMutation.isPending || resendMutation.isSuccess}
              onClick={() => resendMutation.mutate()}
              className="shrink-0 text-xs font-medium text-amber-300 border border-amber-700 rounded-md px-3 py-1.5 hover:bg-amber-900/40 disabled:opacity-50 transition"
            >
              {resendMutation.isSuccess
                ? tD('email_verification.resend_ok')
                : tD('email_verification.resend')}
            </button>
          </div>
        </div>
      )}
      <div className="max-w-6xl mx-auto px-4 py-6 sm:py-10 grid gap-6 md:grid-cols-[220px_minmax(0,1fr)]">
        <aside className="flex md:flex-col gap-1 overflow-x-auto md:overflow-visible -mx-4 md:mx-0 px-4 md:px-0 md:sticky md:top-20 md:self-start">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === `/${lang}/dashboard`}
              className={({ isActive }) =>
                [
                  'flex items-center gap-2 px-3 py-2 rounded-lg text-sm whitespace-nowrap transition',
                  isActive
                    ? 'bg-neutral-900 text-neutral-50'
                    : 'text-neutral-400 hover:text-neutral-100 hover:bg-neutral-900/60',
                ].join(' ')
              }
            >
              <span className="opacity-60">{it.icon}</span>
              {it.label}
            </NavLink>
          ))}
          <div className="hidden md:block mt-4 pt-4 border-t border-neutral-900 text-xs text-neutral-500">
            <p className="truncate">{user?.email}</p>
            {org && <p className="text-neutral-600 mt-1">org: {org.id.slice(-6)}</p>}
            <button
              type="button"
              onClick={() => {
                void logout().then(() => {
                  window.location.href = `/${lang}`;
                });
              }}
              className="mt-3 text-neutral-400 hover:text-neutral-200"
            >
              {t('nav.logout')}
            </button>
          </div>
        </aside>
        <section className="min-w-0">{children}</section>
      </div>
      <div className="md:hidden px-4 pb-8 text-xs text-neutral-500 max-w-6xl mx-auto">
        {user?.email}
        {' · '}
        <Link
          to={`/${lang}`}
          onClick={() => {
            void logout();
          }}
          className="hover:text-neutral-200 underline"
        >
          {t('nav.logout')}
        </Link>
      </div>
    </Layout>
  );
}

function HomeIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 12l9-9 9 9" />
      <path d="M5 10v10h14V10" />
    </svg>
  );
}
function CalendarIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  );
}
function CoinIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M9 9h4.5a2.5 2.5 0 010 5H9M12 7v10" />
    </svg>
  );
}
function ShieldIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}
function CogIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33h0a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51h0a1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82v0a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z" />
    </svg>
  );
}
function MailIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="M2 7l10 7 10-7" />
    </svg>
  );
}
